import { useEffect, useRef, useState } from "react";
import { validArtworkPath } from "../api";
import { withRequestTimeout } from "../json";
import {
  artworkSessionIsCurrent,
  cacheArtwork,
  cachedArtwork,
  cachedOfflineSessionContext,
  deleteCachedArtwork,
  validArtworkBlob,
  type OfflineSessionContext,
} from "../storage";
import type { ArtworkRef } from "../types";

const MAX_ARTWORK_RESPONSE_BYTES = 64 * 1024 * 1024;
const MAX_ARTWORK_RESPONSE_CHUNKS = 16_384;

interface ArtworkFetchResult {
  blob: Blob;
  status: number;
}

interface ArtworkLoadResult {
  blob: Blob;
  persistence?: Promise<void>;
}

interface ArtworkFetchFlight {
  controller: AbortController;
  promise: Promise<ArtworkFetchResult>;
  consumers: number;
  settled: boolean;
}

const artworkFetchFlights = new Map<string, ArtworkFetchFlight>();

export function ArtworkImage({
  artwork,
  variant = "color",
  className,
  loading = "lazy",
  ownerItemId,
}: {
  artwork: ArtworkRef;
  variant?: "color" | "eink";
  className: string;
  loading?: "eager" | "lazy";
  ownerItemId?: string;
}) {
  const path = variant === "color" ? artwork.color_path : artwork.eink_path;
  const pathIsValid = validArtworkPath(path, variant);
  const host = useRef<HTMLSpanElement>(null);
  const [loadPath, setLoadPath] = useState(() => loading === "eager" && pathIsValid ? path : "");
  const [source, setSource] = useState("");
  const [failed, setFailed] = useState(false);
  const objectURLs = useRef(new Map<string, {
    userId: string;
    sessionGeneration: string;
    path: string;
    persistence?: Promise<void>;
  }>());

  useEffect(() => {
    if (!pathIsValid) {
      setLoadPath("");
      return;
    }
    if (loading === "eager" || !("IntersectionObserver" in window)) {
      setLoadPath(path);
      return;
    }
    setLoadPath("");
    const element = host.current;
    if (!element) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setLoadPath(path);
      observer.disconnect();
    }, { rootMargin: "600px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, path, pathIsValid]);

  useEffect(() => {
    if (!pathIsValid || loadPath !== path) return;
    let active = true;
    let objectURL = "";
    const controller = new AbortController();
    setSource("");
    setFailed(false);
    void cachedOfflineSessionContext().then(async (context) => {
      if (!context) throw new Error("artwork_session_unavailable");
      return { loaded: await loadArtwork(context, path, controller.signal, ownerItemId), context };
    }).then(({ loaded, context }) => {
      if (!active) return;
      objectURL = URL.createObjectURL(loaded.blob);
      objectURLs.current.set(objectURL, {
        userId: context.session.user_id,
        sessionGeneration: context.generation,
        path,
        persistence: loaded.persistence,
      });
      setSource(objectURL);
    }).catch((error) => {
      if (active && !(error instanceof DOMException && error.name === "AbortError")) setFailed(true);
    });
    return () => {
      active = false;
      controller.abort();
      if (objectURL && objectURLs.current.delete(objectURL)) URL.revokeObjectURL(objectURL);
    };
  }, [loadPath, ownerItemId, path, pathIsValid]);

  if (failed || !pathIsValid) return null;
  const focalX = finiteFocalPoint(artwork.focal_x);
  const focalY = finiteFocalPoint(artwork.focal_y);
  return (
    <span ref={host} className={`${className}${source ? " artwork-image--ready" : ""}`} aria-hidden="true">
      {source ? (
        <img
          src={source}
          alt=""
          loading={loading}
          onError={() => {
            const context = objectURLs.current.get(source);
            if (objectURLs.current.delete(source)) URL.revokeObjectURL(source);
            if (context) {
              void (async () => {
                await context.persistence;
                await deleteCachedArtwork(context.userId, context.path, context.sessionGeneration);
              })().catch(() => undefined);
            }
            setSource("");
            setFailed(true);
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: `${focalX * 100}% ${focalY * 100}%` }}
        />
      ) : null}
    </span>
  );
}

function finiteFocalPoint(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0.5;
}

async function loadArtwork(
  context: OfflineSessionContext,
  path: string,
  signal: AbortSignal,
  ownerItemId?: string,
): Promise<ArtworkLoadResult> {
  const userId = context.session.user_id;
  if (!navigator.onLine) {
    const blob = await cachedArtwork(userId, path, ownerItemId, context.generation);
    if (!isImageBlob(blob) || !(await validArtworkBlob(blob))) throw new Error("artwork_unavailable");
    await requireCurrentSession(context, ownerItemId);
    return { blob };
  }
  try {
    const { blob } = await sharedNetworkArtwork(context, path, signal);
    await requireCurrentSession(context, ownerItemId);
    // Persistence is deliberately background work: validated authenticated bytes can paint
    // while IndexedDB performs image decoding, accounting, and eviction.
    const persistence = cacheArtwork(userId, path, blob, ownerItemId, context.generation).then(
      () => undefined,
      () => undefined,
    );
    return { blob, persistence };
  } catch (error) {
    if (signal.aborted) throw error;
    if (typeof error === "object" && error !== null && "status" in error && Number((error as { status?: unknown }).status) < 500) throw error;
    const blob = await cachedArtwork(userId, path, ownerItemId, context.generation);
    await requireCurrentSession(context, ownerItemId);
    if (isImageBlob(blob) && await validArtworkBlob(blob)) return { blob };
    throw error;
  }
}

async function sharedNetworkArtwork(
  context: OfflineSessionContext,
  path: string,
  signal: AbortSignal,
): Promise<ArtworkFetchResult> {
  const key = `${context.generation}\u0000${context.session.user_id}\u0000${path}`;
  let flight = artworkFetchFlights.get(key);
  if (!flight) {
    const controller = new AbortController();
    const promise = fetchNetworkArtwork(path, controller.signal);
    flight = { controller, promise, consumers: 0, settled: false };
    artworkFetchFlights.set(key, flight);
    const createdFlight = flight;
    void promise.then(
      () => settleArtworkFetch(key, createdFlight),
      () => settleArtworkFetch(key, createdFlight),
    );
  }
  flight.consumers += 1;
  try {
    return await waitForArtworkFetch(flight.promise, signal);
  } finally {
    flight.consumers -= 1;
    if (!flight.settled && flight.consumers === 0) {
      flight.controller.abort();
      if (artworkFetchFlights.get(key) === flight) artworkFetchFlights.delete(key);
    }
  }
}

function fetchNetworkArtwork(path: string, signal: AbortSignal): Promise<ArtworkFetchResult> {
  return withRequestTimeout(signal, async (requestSignal) => {
    const response = await fetch(path, { credentials: "same-origin", signal: requestSignal });
    if (!response.ok) throw artworkResponseError("artwork_unavailable", response.status);
    const blob = await readBoundedArtworkResponse(response);
    if (!isImageBlob(blob) || !(await validArtworkBlob(blob, true))) {
      throw artworkResponseError("artwork_invalid", response.status);
    }
    return { blob, status: response.status };
  });
}

function settleArtworkFetch(key: string, flight: ArtworkFetchFlight): void {
  flight.settled = true;
  if (artworkFetchFlights.get(key) === flight) artworkFetchFlights.delete(key);
}

function waitForArtworkFetch(promise: Promise<ArtworkFetchResult>, signal: AbortSignal): Promise<ArtworkFetchResult> {
  if (signal.aborted) return Promise.reject(abortedArtworkRequest());
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (operation: () => void) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", onAbort);
      operation();
    };
    const onAbort = () => finish(() => reject(abortedArtworkRequest()));
    signal.addEventListener("abort", onAbort, { once: true });
    void promise.then(
      (result) => finish(() => resolve(result)),
      (error) => finish(() => reject(error)),
    );
  });
}

function abortedArtworkRequest(): DOMException {
  return new DOMException("The artwork request was aborted.", "AbortError");
}

export async function readBoundedArtworkResponse(
  response: Response,
  maxBytes = MAX_ARTWORK_RESPONSE_BYTES,
  maxChunks = MAX_ARTWORK_RESPONSE_CHUNKS,
): Promise<Blob> {
  if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0 || !Number.isSafeInteger(maxChunks) || maxChunks <= 0) {
    throw new RangeError("invalid_artwork_response_bounds");
  }
  const declaredLength = response.headers.get("content-length")?.trim();
  const declaredLengthValue = declaredLength && /^\d+$/.test(declaredLength) && declaredLength.length <= 20
    ? Number(declaredLength)
    : Number.NaN;
  if (declaredLength && (!Number.isSafeInteger(declaredLengthValue) || declaredLengthValue > maxBytes)) {
    void response.body?.cancel("artwork_response_too_large").catch(() => undefined);
    throw artworkResponseError("artwork_response_too_large", response.status);
  }
  if (!response.body) throw artworkResponseError("artwork_invalid", response.status);

  const reader = response.body.getReader();
  const chunks: ArrayBuffer[] = [];
  let total = 0;
  let chunkCount = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      chunkCount += 1;
      if (chunkCount > maxChunks) {
        void reader.cancel("artwork_response_too_fragmented").catch(() => undefined);
        throw artworkResponseError("artwork_response_too_fragmented", response.status);
      }
      if (!value.byteLength) continue;
      total += value.byteLength;
      if (total > maxBytes) {
        void reader.cancel("artwork_response_too_large").catch(() => undefined);
        throw artworkResponseError("artwork_response_too_large", response.status);
      }
      const copy = new Uint8Array(value.byteLength);
      copy.set(value);
      chunks.push(copy.buffer);
    }
  } finally {
    reader.releaseLock();
  }
  return new Blob(chunks, { type: response.headers.get("content-type") || "" });
}

function artworkResponseError(code: string, status: number): Error & { status: number } {
  const error = new Error(code) as Error & { status: number };
  error.status = status;
  return error;
}

async function requireCurrentSession(context: OfflineSessionContext, ownerItemId?: string): Promise<void> {
  if (!(await artworkSessionIsCurrent(context.session.user_id, context.generation, ownerItemId))) {
    throw new Error("stale_artwork_session");
  }
}

function isImageBlob(blob?: Blob): blob is Blob {
  return Boolean(blob
    && blob.size > 0
    && blob.size <= MAX_ARTWORK_RESPONSE_BYTES
    && (blob.type === "image/png" || blob.type === "image/jpeg"));
}
